/**
 * /docs/webhooks -- developer docs for receiving + verifying Praxara webhooks.
 *
 * Shipped alongside the Settings -> Webhooks UI so a customer's developer can
 * stand up a verified receiver without a meeting. Three things they need:
 *   1. Headers we send + payload shape.
 *   2. How to verify HMAC-SHA-256 signature (constant-time compare).
 *   3. Verified Node.js + Python receiver examples.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Webhook, Copy, Check, ExternalLink, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const NODE_SNIPPET = `// Node.js (Express) -- verify a Praxara webhook
import crypto from 'node:crypto';
import express from 'express';

const SECRET = process.env.PRAXARA_WEBHOOK_SECRET; // 96 hex chars

// Capture the raw body BEFORE express.json() parses it -- HMAC is computed
// over the exact bytes Praxara sent.
const app = express();
app.post('/praxara', express.raw({ type: 'application/json' }), (req, res) => {
  const ts = req.header('x-praxara-timestamp');
  const sig = req.header('x-praxara-signature') || '';
  const event = req.header('x-praxara-event');
  if (!ts || !sig) return res.sendStatus(400);

  // Reject deliveries older than 5 minutes (replay protection).
  const skewMs = Math.abs(Date.now() - new Date(ts).getTime());
  if (skewMs > 5 * 60_000) return res.sendStatus(401);

  const body = req.body.toString('utf8');
  const expected = 'sha256=' + crypto
    .createHmac('sha256', SECRET)
    .update(ts + '.' + body)
    .digest('hex');

  // Constant-time compare -- prevents timing attacks.
  const ok = sig.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected));
  if (!ok) return res.sendStatus(401);

  const payload = JSON.parse(body);
  console.log('verified event', event, payload.data);
  res.sendStatus(200); // ANY 2xx marks the delivery as successful.
});

app.listen(3000);`;

const PYTHON_SNIPPET = `# Python (Flask) -- verify a Praxara webhook
import hmac, hashlib, os, time
from datetime import datetime, timezone
from flask import Flask, request, abort

SECRET = os.environ['PRAXARA_WEBHOOK_SECRET'].encode()  # 96 hex chars

app = Flask(__name__)

@app.post('/praxara')
def receive():
    ts = request.headers.get('X-Praxara-Timestamp')
    sig = request.headers.get('X-Praxara-Signature', '')
    event = request.headers.get('X-Praxara-Event')
    if not ts or not sig:
        abort(400)

    # Reject deliveries older than 5 minutes (replay protection).
    sent_at = datetime.fromisoformat(ts.replace('Z', '+00:00'))
    skew = abs((datetime.now(timezone.utc) - sent_at).total_seconds())
    if skew > 300:
        abort(401)

    body = request.get_data()  # raw bytes -- DO NOT use request.json
    expected = 'sha256=' + hmac.new(
        SECRET, ts.encode() + b'.' + body, hashlib.sha256
    ).hexdigest()

    if not hmac.compare_digest(sig, expected):
        abort(401)

    payload = request.get_json()
    print('verified event', event, payload['data'])
    return ('', 200)  # ANY 2xx marks the delivery as successful.`;

const CURL_SNIPPET = `# A single delivery, as Praxara sends it
POST https://your-system.example.com/praxara
Content-Type: application/json
User-Agent: Praxara-Webhooks/1.0
X-Praxara-Event: workflow.run.completed
X-Praxara-Delivery: 7f1c5e4a-2b9d-4f3a-8c1e-2b3a4c5d6e7f
X-Praxara-Timestamp: 2026-05-02T12:00:00.000Z
X-Praxara-Signature: sha256=8b3f1...

{
  "id": "delivery-uuid",
  "tenantId": "tenant-uuid",
  "event": "workflow.run.completed",
  "timestamp": "2026-05-02T12:00:00.000Z",
  "attempt": 1,
  "data": {
    "workflowRunId": "...",
    "workflowId": "...",
    "workflowName": "EU AI Act compliance scan",
    "completedSteps": 4
  }
}`;

export function WebhookDocsPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-xs uppercase tracking-wider text-muted-foreground">
          <Webhook className="h-3.5 w-3.5" /> Developer docs
        </div>
        <h1 className="font-display text-3xl">Receiving Praxara webhooks</h1>
        <p className="text-muted-foreground">
          Praxara POSTs JSON to your endpoint when subscribed events fire. Every request is signed
          with HMAC-SHA-256 using your per-subscription secret -- verify before acting.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link to="/settings/webhooks"><Button size="sm" variant="outline">Manage webhooks <ExternalLink className="ml-1 h-3 w-3" /></Button></Link>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle><span className="inline-flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Headers we send</span></CardTitle>
          <CardDescription>Every delivery carries these. The signature is the load-bearing one.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <HeaderRow name="X-Praxara-Event" value="workflow.run.completed" desc="The event name; matches what you subscribed to." />
          <HeaderRow name="X-Praxara-Delivery" value="<uuid>" desc="Unique delivery id. Use to dedupe + idempotently process retries." />
          <HeaderRow name="X-Praxara-Timestamp" value="2026-05-02T12:00:00.000Z" desc="ISO-8601. Reject deliveries older than ~5 minutes (replay protection)." />
          <HeaderRow name="X-Praxara-Signature" value="sha256=<hex>" desc="HMAC-SHA-256 over `${'${timestamp}.${body}'}`. Constant-time-compare." />
          <HeaderRow name="User-Agent" value="Praxara-Webhooks/1.0" desc="Useful for filtering in your edge logs." />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Example delivery</CardTitle>
          <CardDescription>The raw HTTP request. <code>data</code> is event-specific.</CardDescription>
        </CardHeader>
        <CardContent>
          <CodeBlock language="http" code={CURL_SNIPPET} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verifying the signature</CardTitle>
          <CardDescription>Compute <code>sha256=hex(HMAC_SHA256(secret, timestamp + "." + body))</code>, then constant-time compare.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="mb-1 flex items-center justify-between"><Badge variant="outline" className="font-mono">Node.js (Express)</Badge></div>
            <CodeBlock language="javascript" code={NODE_SNIPPET} />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between"><Badge variant="outline" className="font-mono">Python (Flask)</Badge></div>
            <CodeBlock language="python" code={PYTHON_SNIPPET} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Retries + auto-deactivation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <ul className="list-disc space-y-1 pl-5">
            <li><strong>Success:</strong> any 2xx response marks the delivery as successful.</li>
            <li><strong>Permanent failure:</strong> 4xx (except 408 + 429) -- not retried; counts toward auto-disable.</li>
            <li><strong>Retryable failure:</strong> 5xx, 408, 429, network timeout (10s) -- retried with backoff <code>30s -&gt; 2m -&gt; 10m -&gt; 1h -&gt; 6h</code> (5 attempts max).</li>
            <li><strong>Auto-disable:</strong> after 5 consecutive failed deliveries we set <code>isActive=false</code> and audit-log <code>WEBHOOK_SUBSCRIPTION_DEACTIVATED</code>. Re-enable from Settings -&gt; Webhooks once your endpoint is healthy.</li>
            <li><strong>Idempotency:</strong> use <code>X-Praxara-Delivery</code> as your idempotency key so retries don't double-process.</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Subscribable events</CardTitle>
          <CardDescription>The full catalogue. New events appear in the changelog.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="grid gap-1 text-sm md:grid-cols-2">
            {[
              ['webhook.test', 'Test ping fired manually from Settings'],
              ['workflow.run.started', 'A workflow run was queued'],
              ['workflow.run.completed', 'A workflow run completed all steps'],
              ['workflow.run.failed', 'A workflow run failed mid-execution'],
              ['extraction.review.completed', 'A reviewer approved or applied corrections'],
              ['extraction.review.rejected', 'A reviewer rejected an extraction'],
              ['document.uploaded', 'A document was uploaded'],
              ['document.processed', 'Text extraction completed'],
              ['pv.case.created', 'A new PV case was opened'],
              ['pv.case.updated', 'A PV case was updated (followup, status, etc.)'],
              ['compliance.scan.completed', 'A compliance scan finished'],
              ['pack.rendered', 'A compliance pack was rendered + ready'],
            ].map(([ev, desc]) => (
              <li key={ev} className="rounded border p-2">
                <code className="text-xs font-mono">{ev}</code>
                <div className="text-xs text-muted-foreground">{desc}</div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function HeaderRow({ name, value, desc }: { name: string; value: string; desc: string }) {
  return (
    <div className="grid grid-cols-[200px_1fr] items-start gap-3 border-b py-1.5 last:border-0">
      <code className="text-xs font-mono text-foreground">{name}</code>
      <div>
        <code className="text-[11px] text-muted-foreground">{value}</code>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
    </div>
  );
}

function CodeBlock({ code }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <div className="relative rounded-md border bg-muted/30">
      <div className="absolute right-2 top-2">
        <Button size="sm" variant="outline" onClick={copy}>
          {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="overflow-x-auto p-3 pr-20 text-[11px] leading-relaxed"><code>{code}</code></pre>
    </div>
  );
}
